import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, CheckCircle, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import AuthService from '@/lib/auth';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

export function EmailVerification() {
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [code, setCode] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';

  useEffect(() => {
    if (!email) {
      toast.error("No email provided for verification");
      navigate('/login');
      return;
    }

    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  const handleVerify = async () => {
    if (!code || code.length < 6) {
      toast.error("Please enter the full confirmation code");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await AuthService.confirmSignUp(email, code);

      if (error) {
        throw error;
      }

      setIsVerified(true);
      toast.success("Account verified successfully!", {
        description: "Redirecting to login..."
      });

      // Brief delay before redirect
      setTimeout(() => navigate('/login'), 2000);

    } catch (error: any) {
      console.error("Verification error:", error);
      toast.error(error.message || "Failed to verify code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!email || !canResend) return;

    setIsLoading(true);
    try {
      const { error } = await AuthService.resendSignUpCode(email);

      if (error) {
        throw error;
      }

      setCountdown(60);
      setCanResend(false);

      toast.success('Code resent!', {
        description: 'A new verification code has been sent to your email.'
      });

      // Restart countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (error: any) {
      console.error('Error resending code:', error);
      toast.error(error.message || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-700">Verified!</CardTitle>
            <CardDescription>
              Your email has been verified successfully.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Redirecting to login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <Smartphone className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Verify your account</CardTitle>
          <CardDescription>
            We've sent a verification code to <br />
            <span className="font-semibold text-foreground">{email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <Input
              type="text"
              placeholder="Enter confirmation code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isLoading}
              className="text-center text-lg tracking-widest"
              maxLength={10}
            />
          </div>

          <Button
            onClick={handleVerify}
            disabled={isLoading || code.length < 6}
            className="w-full bg-[#12BB81] hover:bg-[#0fa371]"
          >
            {isLoading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify Email"
            )}
          </Button>

          <div className="text-center text-sm">
            <p className="text-muted-foreground mb-2">Didn't receive the code?</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResendEmail}
              disabled={!canResend || isLoading}
              className="gap-2"
            >
              <Mail className="h-4 w-4" />
              {canResend ? 'Resend Code' : `Resend in ${countdown}s`}
            </Button>
          </div>
        </CardContent>
        <CardFooter className="justify-center border-t pt-4">
          <Button variant="link" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export default EmailVerification;
